import {
  Button,
  Col,
  Divider,
  Form,
  Input,
  Radio,
  Row,
  Select,
  Space,
  Typography,
} from 'antd'
import {
  DeleteOutlined,
  LoadingOutlined,
  PlusOutlined,
} from '@ant-design/icons'
import { Suspense, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useFirestoreDocData, useFirestore } from 'reactfire'
import { doc } from 'firebase/firestore'
import { formGutter } from '../../lib/layout'
import { TaxOfficeDetails } from '../TaxOfficeDetails'
import { TaxOfficeFormItem } from './TaxOfficeFormItem'
import {
  chartOfAccountsOptions,
  incomeRegistrationPeriodOptions,
} from './companyFormOptions'
import { isDatevPayrollSoftware } from '../../../functions/src/lib/shared/isDatevPayrollSoftware'
import { oneTimePaymentType } from '../../lib/shared/models/OneTimePayment'
import { hourlyRateSlots } from '../../lib/shared/hourlyRateSlots'
import {
  coercePaymentLohnarten,
  paymentLohnartHourlyRateSlot,
  paymentLohnartUnit,
  paymentLohnartUnits,
  payrollNumbersMatchBookkeeping,
  resolvePayrollConsultantNumber,
  resolvePayrollMandateNumber,
} from '../../lib/shared/models/Company'
import {
  findSalaryLohnartCollision,
  lodasPaymentLohnarten,
} from '../../lib/shared/lodasSalaryLohnart'
import { useHasSalariedContracts } from './useHasSalariedContracts'

const toLohnart = (value) => {
  const parsed =
    typeof value === 'number' ? value : Number.parseInt(String(value ?? ''), 10)
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null
}

/** A typed Lohnart only once it is all digits; anything else is the pattern rule's. */
const typedLohnart = (value) =>
  /^\d+$/.test(String(value ?? '').trim()) ? toLohnart(value) : null

// A row the operator started and did not finish maps nothing, so it is dropped
// rather than saved looking as though it did. Not `coercePaymentLohnarten`:
// that reads stored data and wants numbers, while a typed Lohnart arrives here
// as the string "48". Coercing first threw every typed row away before
// `toLohnart` could parse it, so a save stored an empty list.
const toPaymentLohnarten = (entries) =>
  (entries ?? []).flatMap((entry) => {
    const lohnart = toLohnart(entry?.lohnart)
    return lohnart !== null && entry?.type
      ? [
          {
            lohnart,
            type: entry.type,
            unit: paymentLohnartUnit(entry),
            hourlyRateSlot: paymentLohnartHourlyRateSlot(entry),
          },
        ]
      : []
  })

/**
 * The setting the typed salary Lohnart shares its number with, resolved the way
 * the LODAS export resolves it: the export drops the salary in that case.
 */
const salaryLohnartCollision = (datev, commissionLohnart) =>
  findSalaryLohnartCollision(typedLohnart(datev?.salaryLohnart), {
    vacation: typedLohnart(datev?.vacationLohnart),
    sickness: typedLohnart(datev?.sicknessLohnart),
    payments: lodasPaymentLohnarten({
      paymentLohnarten: toPaymentLohnarten(datev?.paymentLohnarten),
      commissionLohnart,
    }),
  })

export default function CompanyAccountingForm({
  sendFormValues,
  isFinalStep,
  loading,
  data,
  requireDatevNumbers,
  operatorContext = false,
  goBack,
  onCancel,
}) {
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const firestore = useFirestore()

  const payrollFields = operatorContext && isDatevPayrollSoftware(data)

  // Firestore reads are not validated against the schema, so the field arrives
  // in whatever shape the company was last written under. Coerced here as
  // well: an editor that rendered empty would save an empty list back over
  // whatever was there.
  const initialValues = data?.datev
    ? {
        ...data,
        ...(payrollFields
          ? {
              payrollNumbersMatchFibu: payrollNumbersMatchBookkeeping(
                data.datev
              ),
            }
          : {}),
        datev: {
          ...data.datev,
          paymentLohnarten: coercePaymentLohnarten(data.datev.paymentLohnarten),
          ...(payrollFields
            ? {
                // The numbers payroll actually runs under: a client without a
                // Lohn pair of its own shows the FiBu numbers here, which is
                // what it runs under.
                payrollUserId: resolvePayrollMandateNumber(data.datev),
                payrollConsultantId: resolvePayrollConsultantNumber(data.datev),
              }
            : {}),
        },
      }
    : data

  const { data: county } = useFirestoreDocData(
    doc(
      firestore,
      'configs',
      'addresses',
      'zip-to-county',
      data.address?.zipCode ?? '00000'
    ),
    { idField: 'uid' }
  )

  const taxId = Form.useWatch(['accounting', 'taxId'], {
    form,
    preserve: true,
  })

  const taxOfficeNumber = Form.useWatch(['accounting', 'taxOfficeNumber'], {
    form,
    preserve: true,
  })

  function handleFormValuesChange(values) {
    // An hourly wage is booked in hours, so its unit follows the type. Only
    // hours are valued with a Stundenlohn, so a row that stops being one goes
    // back to Stundenlohn 1 rather than keeping a slot nothing reads.
    const changedRows = values?.datev?.paymentLohnarten
    if (Array.isArray(changedRows)) {
      changedRows.forEach((row, index) => {
        if (row?.type === 'HourlyWage') {
          form.setFieldValue(
            ['datev', 'paymentLohnarten', index, 'unit'],
            'hours'
          )
        } else if (row?.unit !== undefined && row.unit !== 'hours') {
          form.setFieldValue(
            ['datev', 'paymentLohnarten', index, 'hourlyRateSlot'],
            1
          )
        }
      })
    }

    if (values?.datev?.using === false) {
      form.setFieldsValue({
        datev: {
          userId: null,
          consultantId: null,
        },
      })
    }
  }

  async function onFinish(values) {
    const payrollUserId = values.datev.payrollUserId?.trim() || null
    const payrollConsultantId = values.datev.payrollConsultantId?.trim() || null
    // One pair for both Bereiche: stored as the FiBu pair, with the Lohn pair
    // left null so the resolvers fall back to it and every bookkeeping path
    // keeps reading the numbers where it always did. Only meaningful while the
    // FiBu is kept in DATEV — with `using` off that pair is cleared, so the
    // numbers are stored as the Lohn pair instead of being thrown away.
    const oneNumberPair =
      payrollFields &&
      values.datev.using &&
      values.payrollNumbersMatchFibu !== false

    const finalValues = {
      accounting: {
        taxId: values.accounting.taxId.trim(),
        incomeRegistrationPeriod: values.accounting.incomeRegistrationPeriod,
        chartOfAccounts: values.accounting.chartOfAccounts ?? null,
        taxOfficeNumber: values.accounting.taxOfficeNumber ?? null,
      },
      datev: {
        using: values.datev.using,
        userId: oneNumberPair
          ? payrollUserId
          : values.datev.userId?.trim() || null,
        consultantId: oneNumberPair
          ? payrollConsultantId
          : values.datev.consultantId?.trim() || null,
        ...(payrollFields
          ? {
              // The Lohn pair. Left out for a non-LODAS client rather than
              // sent as null: only the payroll paths read it, and the fields
              // are not on that form to be cleared from.
              payrollUserId: oneNumberPair ? null : payrollUserId,
              payrollConsultantId: oneNumberPair ? null : payrollConsultantId,
              salaryLohnart: toLohnart(values.datev.salaryLohnart),
              vacationLohnart: toLohnart(values.datev.vacationLohnart),
              sicknessLohnart: toLohnart(values.datev.sicknessLohnart),
              paymentLohnarten: toPaymentLohnarten(
                values.datev.paymentLohnarten
              ),
            }
          : {}),
      },
    }

    sendFormValues(finalValues, isFinalStep, data?.uid)
  }

  const usingDatev = Form.useWatch(['datev', 'using'], {
    form,
    preserve: true,
  })

  const payrollNumbersMatchFibu = Form.useWatch('payrollNumbersMatchFibu', {
    form,
    preserve: true,
  })

  const showFibuNumberFields =
    payrollFields && usingDatev && payrollNumbersMatchFibu === false

  const datevValues = Form.useWatch('datev', { form, preserve: true })
  const hasSalariedContracts = useHasSalariedContracts(data?.uid, payrollFields)
  const commissionLohnart = data?.datev?.commissionLohnart ?? null

  const collisionMessage = (collision) =>
    collision.with === 'payment'
      ? t('frontend:company.salaryLohnartCollidesPayment', {
          setting: t(`frontend:oneTimePayments.paymentType.${collision.type}`),
        })
      : t('frontend:company.salaryLohnartCollides', {
          setting: t(`frontend:company.${collision.with}Lohnart`),
        })

  // Shown from the moment the form opens, not only after a save attempt: the
  // settings are usually already wrong when somebody comes looking.
  const salaryCollision = payrollFields
    ? salaryLohnartCollision(datevValues, commissionLohnart)
    : null
  const salaryLohnartEmpty =
    String(datevValues?.salaryLohnart ?? '').trim() === ''
  const salaryLohnartHelp = salaryCollision
    ? collisionMessage(salaryCollision)
    : payrollFields && salaryLohnartEmpty && hasSalariedContracts === true
      ? t('frontend:company.salaryLohnartRequired')
      : undefined

  // The collision can be resolved in another field (a payment row, the EFZ
  // Lohnarten), which antd does not treat as a dependency of this one, so a
  // rejected save would otherwise keep showing the old error.
  const collisionKey = salaryCollision
    ? `${salaryCollision.with}:${salaryCollision.type ?? ''}:${salaryCollision.lohnart}`
    : null
  useEffect(() => {
    if (form.getFieldError(['datev', 'salaryLohnart']).length > 0) {
      form.validateFields([['datev', 'salaryLohnart']]).catch(() => {})
    }
  }, [collisionKey, form])

  return (
    <Form
      form={form}
      layout="vertical"
      initialValues={initialValues}
      onValuesChange={handleFormValuesChange}
      onFinish={onFinish}
    >
      <Row gutter={formGutter}>
        <Col xs={24} md={12}>
          <TaxIdFormItem
            county={county}
            taxId={taxId}
            taxOfficeNumber={taxOfficeNumber}
          />
        </Col>
        <Col xs={24} md={12}>
          <Suspense fallback={<LoadingOutlined />}>
            <TaxOfficeFormItem county={county?.county} taxId={taxId} />
          </Suspense>
        </Col>
        <Col xs={24} md={12}>
          <IncomeRegistrationPeriodFormItem />
        </Col>
        <Col xs={24} md={12}>
          <ChartOfAccountsFormItem />
        </Col>
        <Divider />
        <Col xs={24}>
          <DatevUsingFormItem />
        </Col>
        {!payrollFields && (
          <>
            <Col xs={24} md={12}>
              <DatevClientNumberFormItem
                required={requireDatevNumbers && usingDatev}
                disabled={!usingDatev}
              />
            </Col>
            <Col xs={24} md={12}>
              <DatevConsultantNumberFormItem
                required={requireDatevNumbers && usingDatev}
                disabled={!usingDatev}
              />
            </Col>
          </>
        )}
        {payrollFields && (
          <>
            {/* For a LODAS client the Lohn numbers are asked for first: they
                are the ones payroll runs under, and DATEV lets a Kanzlei file
                them separately from the bookkeeping ones. Most clients run
                both Bereiche under one pair, so the switch below stays on Ja
                and the FiBu fields stay out of the way. */}
            <Col xs={24} md={12}>
              <Form.Item
                label={t('frontend:company.datevPayrollConsultantNumber')}
                rules={[
                  {
                    required: requireDatevNumbers && usingDatev,
                    whitespace: true,
                    message: t('frontend:company.required'),
                  },
                  {
                    pattern: /^\d*$/,
                    message: t('frontend:company.datevNumberInvalid'),
                  },
                ]}
                name={['datev', 'payrollConsultantId']}
              >
                <Input inputMode="numeric" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label={t('frontend:company.datevPayrollClientNumber')}
                rules={[
                  {
                    required: requireDatevNumbers && usingDatev,
                    whitespace: true,
                    message: t('frontend:company.required'),
                  },
                  {
                    pattern: /^\d*$/,
                    message: t('frontend:company.datevNumberInvalid'),
                  },
                ]}
                name={['datev', 'payrollUserId']}
              >
                <Input inputMode="numeric" />
              </Form.Item>
            </Col>
            {usingDatev && (
              <Col xs={24}>
                <Form.Item
                  label={t('frontend:company.datevSameNumbersForFibu')}
                  name="payrollNumbersMatchFibu"
                >
                  <Radio.Group buttonStyle="solid">
                    <Radio.Button id="payrollNumbersMatchFibu" value={true}>
                      {t('frontend:common.yes')}
                    </Radio.Button>
                    <Radio.Button
                      id="payrollNumbersDifferFromFibu"
                      value={false}
                    >
                      {t('frontend:common.no')}
                    </Radio.Button>
                  </Radio.Group>
                </Form.Item>
              </Col>
            )}
            {showFibuNumberFields && (
              <>
                <Col xs={24} md={12}>
                  <Form.Item
                    label={t('frontend:company.datevFibuConsultantNumber')}
                    rules={[
                      {
                        required: requireDatevNumbers && usingDatev,
                        whitespace: true,
                        message: t('frontend:company.required'),
                      },
                      {
                        pattern: /^\d+$/,
                        message: t('frontend:company.datevNumberInvalid'),
                      },
                    ]}
                    name={['datev', 'consultantId']}
                  >
                    <Input inputMode="numeric" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    label={t('frontend:company.datevFibuClientNumber')}
                    rules={[
                      {
                        required: requireDatevNumbers && usingDatev,
                        whitespace: true,
                        message: t('frontend:company.required'),
                      },
                      {
                        pattern: /^\d+$/,
                        message: t('frontend:company.datevNumberInvalid'),
                      },
                    ]}
                    name={['datev', 'userId']}
                  >
                    <Input inputMode="numeric" />
                  </Form.Item>
                </Col>
              </>
            )}
            <Col xs={24} md={8}>
              <Form.Item
                label={t('frontend:company.salaryLohnart')}
                tooltip={t('frontend:company.lohnartHint')}
                rules={[
                  {
                    pattern: /^\d*$/,
                    message: t('frontend:company.datevNumberInvalid'),
                  },
                  {
                    // A colliding number cannot be saved: the export would
                    // drop every salary. An empty one can, so an operator who
                    // does not have the client's list yet can still edit the
                    // rest; the message under the field stays until it is set.
                    validator: () => {
                      const collision = salaryLohnartCollision(
                        form.getFieldValue('datev'),
                        commissionLohnart
                      )
                      return collision
                        ? Promise.reject(new Error(collisionMessage(collision)))
                        : Promise.resolve()
                    },
                  },
                ]}
                {...(salaryLohnartHelp
                  ? { validateStatus: 'error', help: salaryLohnartHelp }
                  : {})}
                name={['datev', 'salaryLohnart']}
              >
                <Input inputMode="numeric" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                label={t('frontend:company.vacationLohnart')}
                tooltip={t('frontend:company.lohnartHint')}
                rules={[
                  {
                    pattern: /^\d*$/,
                    message: t('frontend:company.datevNumberInvalid'),
                  },
                ]}
                name={['datev', 'vacationLohnart']}
              >
                <Input inputMode="numeric" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                label={t('frontend:company.sicknessLohnart')}
                tooltip={t('frontend:company.lohnartHint')}
                rules={[
                  {
                    pattern: /^\d*$/,
                    message: t('frontend:company.datevNumberInvalid'),
                  },
                ]}
                name={['datev', 'sicknessLohnart']}
              >
                <Input inputMode="numeric" />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Typography.Text strong>
                {t('frontend:company.paymentLohnarten')}
              </Typography.Text>
              <Typography.Paragraph type="secondary">
                {t('frontend:company.paymentLohnartenHint')}
              </Typography.Paragraph>
              <Form.List name={['datev', 'paymentLohnarten']}>
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(({ key, name, ...rest }) => (
                      <Row key={key} gutter={formGutter} align="middle">
                        <Col xs={5} md={4}>
                          <Form.Item
                            {...rest}
                            name={[name, 'lohnart']}
                            rules={[
                              {
                                pattern: /^\d*$/,
                                message: t(
                                  'frontend:company.datevNumberInvalid'
                                ),
                              },
                            ]}
                          >
                            <Input
                              inputMode="numeric"
                              placeholder={t('frontend:company.lohnartNumber')}
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={7} md={7}>
                          <Form.Item {...rest} name={[name, 'type']}>
                            <Select
                              placeholder={t(
                                'frontend:company.paymentTypePlaceholder'
                              )}
                              options={oneTimePaymentType.map((type) => ({
                                value: type,
                                label: t(
                                  `frontend:oneTimePayments.paymentType.${type}`
                                ),
                              }))}
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={5} md={6}>
                          <Form.Item {...rest} name={[name, 'unit']}>
                            <Select
                              placeholder={t(
                                'frontend:company.paymentLohnartUnitPlaceholder'
                              )}
                              disabled={
                                datevValues?.paymentLohnarten?.[name]?.type ===
                                'HourlyWage'
                              }
                              options={paymentLohnartUnits.map((unit) => ({
                                value: unit,
                                label: t(
                                  `frontend:company.paymentLohnartUnit.${unit}`
                                ),
                              }))}
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={5} md={5}>
                          <Form.Item {...rest} name={[name, 'hourlyRateSlot']}>
                            <Select
                              placeholder={t(
                                'frontend:company.paymentLohnartHourlyRateSlotPlaceholder'
                              )}
                              disabled={
                                paymentLohnartUnit(
                                  datevValues?.paymentLohnarten?.[name] ?? {}
                                ) !== 'hours'
                              }
                              options={hourlyRateSlots.map((slot) => ({
                                value: slot,
                                label: t(
                                  `frontend:company.paymentLohnartHourlyRateSlot.${slot}`
                                ),
                              }))}
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={2}>
                          <Button
                            type="text"
                            icon={<DeleteOutlined />}
                            onClick={() => remove(name)}
                            aria-label={t('frontend:common.delete')}
                          />
                        </Col>
                      </Row>
                    ))}
                    <Button
                      type="dashed"
                      onClick={() => add({ unit: 'amount', hourlyRateSlot: 1 })}
                      icon={<PlusOutlined />}
                      block
                    >
                      {t('frontend:company.addPaymentLohnart')}
                    </Button>
                  </>
                )}
              </Form.List>
            </Col>
          </>
        )}
        <Col xs={24} style={{ textAlign: 'right' }}>
          <Space>
            {onCancel && (
              <Button onClick={onCancel}>{t('frontend:common.cancel')}</Button>
            )}
            {goBack && (
              <Button type="text" onClick={() => goBack(null)}>
                {t('frontend:common.back')}
              </Button>
            )}
            <Button type="primary" htmlType="submit" loading={loading}>
              {isFinalStep
                ? t('frontend:company.save')
                : t('frontend:company.next')}
            </Button>
          </Space>
        </Col>
      </Row>
    </Form>
  )
}

export function TaxIdFormItem({ county, taxId, taxOfficeNumber }) {
  const { t } = useTranslation()

  return (
    <Form.Item
      extra={
        (county || taxOfficeNumber) &&
        taxId &&
        taxId.length >= 3 && (
          <Suspense fallback={<LoadingOutlined />}>
            <TaxOfficeDetails
              county={county?.county}
              taxId={taxId}
              override={taxOfficeNumber}
            />
          </Suspense>
        )
      }
      rules={[
        {
          required: true,
          whitespace: true,
          message: t('frontend:company.required'),
        },
        {
          min: 10,
          max: 13,
          message: t('frontend:company.taxIdLength'),
        },
        {
          pattern: new RegExp(/^[0-9]*$/i),
          message: t('frontend:company.digitsOnly'),
        },
      ]}
      label={t('frontend:company.taxNumber')}
      name={['accounting', 'taxId']}
    >
      <Input />
    </Form.Item>
  )
}

export function IncomeRegistrationPeriodFormItem() {
  const { t } = useTranslation()

  return (
    <Form.Item
      rules={[
        {
          required: true,
          whitespace: true,
          message: t('frontend:company.required'),
        },
      ]}
      label={t('frontend:company.incomeTaxRegistrationPeriod')}
      name={['accounting', 'incomeRegistrationPeriod']}
    >
      <Select
        showSearch
        filterOption={(input, option) =>
          option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
        }
      >
        {incomeRegistrationPeriodOptions.map(({ value, labelKey }) => (
          <Select.Option key={value} value={value}>
            {t(labelKey)}
          </Select.Option>
        ))}
      </Select>
    </Form.Item>
  )
}

export function ChartOfAccountsFormItem() {
  const { t } = useTranslation()

  return (
    <Form.Item
      label={t('frontend:company.chartOfAccounts')}
      name={['accounting', 'chartOfAccounts']}
    >
      <Select
        showSearch
        filterOption={(input, option) =>
          option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
        }
      >
        {chartOfAccountsOptions.map(({ value, label, labelKey }) => (
          <Select.Option key={value} value={value}>
            {labelKey ? t(labelKey) : label}
          </Select.Option>
        ))}
      </Select>
    </Form.Item>
  )
}

export function DatevUsingFormItem() {
  const { t } = useTranslation()

  return (
    <Form.Item
      label={t('frontend:company.datevManagedAccounting')}
      name={['datev', 'using']}
      rules={[
        {
          required: true,
          message: t('frontend:company.required'),
        },
      ]}
    >
      <Radio.Group buttonStyle="solid">
        <Radio.Button id="usingDatev" value={true}>
          {t('frontend:common.yes')}
        </Radio.Button>
        <Radio.Button id="usingNoDatev" value={false}>
          {t('frontend:common.no')}
        </Radio.Button>
      </Radio.Group>
    </Form.Item>
  )
}

export function DatevClientNumberFormItem({ required, disabled }) {
  const { t } = useTranslation()

  return (
    <Form.Item
      label={t('frontend:company.datevClientNumber')}
      rules={[
        {
          required,
          whitespace: true,
          message: t('frontend:company.required'),
        },
        {
          pattern: /^\d+$/,
          message: t('frontend:company.datevNumberInvalid'),
        },
      ]}
      name={['datev', 'userId']}
    >
      <Input disabled={disabled} />
    </Form.Item>
  )
}

export function DatevConsultantNumberFormItem({ required, disabled }) {
  const { t } = useTranslation()

  return (
    <Form.Item
      label={t('frontend:company.datevConsultantNumber')}
      rules={[
        {
          required,
          whitespace: true,
          message: t('frontend:company.required'),
        },
        {
          pattern: /^\d+$/,
          message: t('frontend:company.datevNumberInvalid'),
        },
      ]}
      name={['datev', 'consultantId']}
    >
      <Input disabled={disabled} />
    </Form.Item>
  )
}
